import axios from 'axios';
import { API_BASE_URL } from './config';
import { partnerService } from './partnerService';

class IaaSService {
    constructor() {
        // Nutze direkt axios anstelle einer eigenen Instance
        this.baseURL = API_BASE_URL;
    }
    
    // Hilfsmethode zur Anwendung des Partner-Rabatts
    applyPartnerDiscount(price, isPartner) {
        // 20% Rabatt für Partner bei allen IaaS-Produkten
        if (isPartner) {
            const discountedPrice = price * 0.8; // 20% Rabatt
            return {
                finalPrice: parseFloat(discountedPrice.toFixed(2)),
                originalPrice: parseFloat(price.toFixed(2)),
                discountApplied: true
            };
        }

        // Kein Rabatt für Nicht-Partner
        return {
            finalPrice: price,
            originalPrice: null,
            discountApplied: false
        };
    }

    async getUserServices() {
        return axios.get(`${this.baseURL}/iaas-service/`);
    }

    async getActiveUserServices() {
        return axios.get(`${this.baseURL}/iaas-service/active`);
    }

    calculateNetworkRouterPrice(config) {
        let price = 0;
        switch(config.bandwidth) {
            case '100mbit': price += 10; break;
            case '1gbit': price += 20; break;
        }
        price += config.vpnTunnels * 2;
        if (config.managedFirewall) price += 20;
        if (config.haEnabled) price *= 1.5;

        return price;
    }

    calculatePrivateCloudPrice(config) {
        let price = 0;
        price += config.ram * 2;
        price += config.storage* 0.3;
        price += config.cores * 10;

        if (config.dedicatedHardware) price *= 1.5;
        if (config.haEnabled) price *= 1.8;
        if (config.backupEnabled) price += config.storage * 0.15;

        return price;
    }

    calculateS3StoragePrice(config) {
        let price = 0;
        const basePrice = Math.ceil(parseInt(config.storageSize) / 100) * 2;

        switch(config.redundancyLevel) {
            case 'single': price = basePrice; break;
            case 'double': price = basePrice * 1.5; break;
            case 'triple': price = basePrice * 2; break;
        }

        if (config.cdnEnabled) price += 15;

        return price;
    }

    calculatePBXPrice(config) {
        let price = 0;
        
        // Basispreis basierend auf gleichzeitigen Anrufen
        price += Math.ceil(config.concurrentCalls) * 9;
        
        // Preisanpassung basierend auf der Version
        if (config.version === 'enterprise') {
            // Enterprise Version kostet 50% mehr als die PRO Version
            price *= 1.2;
        }
        
        return price;
    }

    // API-Aufrufe
    async orderService(serviceType, configuration) {
        let basePrice = 0;
        let priceInfo = { finalPrice: 0, originalPrice: null, discountApplied: false };

        // Berechne den Basispreis
        switch(serviceType) {
            case 'windows-server':
            case 'linux-server':
                basePrice = this.calculateServerPrice(configuration);
                break;
            case 'private-network':
                basePrice = this.calculateNetworkRouterPrice(configuration);
                break;
            case 'private-cloud':
                basePrice = this.calculatePrivateCloudPrice(configuration);
                break;
            case 's3-storage':
                basePrice = this.calculateS3StoragePrice(configuration);
                break;
            case 'pbx':
                basePrice = this.calculatePBXPrice(configuration);
                break;
        }

        try {
            // Partner-Status abrufen
            const isPartner = await partnerService.isPartner();
            
            // Rabatt anwenden
            priceInfo = this.applyPartnerDiscount(basePrice, isPartner);
            
            // Zusätzliche Informationen zum Rabatt in die Konfiguration einfügen
            if (priceInfo.discountApplied) {
                configuration.priceInfo = {
                    originalPrice: priceInfo.originalPrice,
                    discountApplied: true
                };
            }

            return axios.post(`${this.baseURL}/iaas-service/order`, {
                serviceType,
                configuration,
                totalPrice: priceInfo.finalPrice
            }, { withCredentials: true });
        } catch (error) {
            console.error('Fehler bei der Preisberechnung:', error);
            // Fallback: Verwende den Basispreis ohne Rabatt
            return axios.post(`${this.baseURL}/iaas-service/order`, {
                serviceType,
                configuration,
                totalPrice: basePrice
            }, { withCredentials: true });
        }
    }

    // Windows Server bestellen
    // Preis wird bereits in der Configure-Komponente berechnet (inkl. Partner-Rabatt)
    async orderWindowsServer(orderDetails) {
        const configuration = {
            ram: orderDetails.ram,
            storage: orderDetails.storage,
            cores: orderDetails.cores,
            managedFirewall: orderDetails.managedFirewall,
            backupEnabled: orderDetails.backupEnabled,
            windowsVersion: orderDetails.windowsVersion,
            rdpUsers: orderDetails.rdpUsers,
            rdsLicenses: orderDetails.rdsLicenses,
            antivirusEnabled: orderDetails.antivirusEnabled
        };

        if (orderDetails.priceInfo) {
            configuration.priceInfo = orderDetails.priceInfo;
        }

        return axios.post(`${this.baseURL}/iaas-service/order`, {
            serviceType: 'windows-server',
            configuration,
            totalPrice: orderDetails.totalPrice,
            ...(orderDetails.endCustomerPrice ? { endCustomerPrice: parseFloat(orderDetails.endCustomerPrice) } : {})
        }, { withCredentials: true });
    }

    // Linux Server bestellen
    // Preis wird bereits in der Configure-Komponente berechnet (inkl. Partner-Rabatt)
    async orderLinuxServer(orderDetails) {
        const configuration = {
            ram: orderDetails.ram,
            storage: orderDetails.storage,
            cores: orderDetails.cores,
            managedFirewall: orderDetails.managedFirewall,
            backupEnabled: orderDetails.backupEnabled,
            distribution: orderDetails.distribution,
            sshKeys: orderDetails.sshKeys,
            containerSupport: orderDetails.containerSupport
        };

        if (orderDetails.priceInfo) {
            configuration.priceInfo = orderDetails.priceInfo;
        }

        return axios.post(`${this.baseURL}/iaas-service/order`, {
            serviceType: 'linux-server',
            configuration,
            totalPrice: orderDetails.totalPrice,
            ...(orderDetails.endCustomerPrice ? { endCustomerPrice: parseFloat(orderDetails.endCustomerPrice) } : {})
        }, { withCredentials: true });
    }

    // Service einem Kunden zuweisen
    async assignServiceToCustomer(serviceId, customerId, endCustomerPrice) {
        try {
            const body = { customerId };
            if (endCustomerPrice && parseFloat(endCustomerPrice) > 0) {
                body.endCustomerPrice = parseFloat(endCustomerPrice);
            }
            const response = await axios.put(
                `${this.baseURL}/iaas-service/assign/${serviceId}`,
                body,
                { withCredentials: true }
            );
            return response.data;
        } catch (error) {
            console.error('Fehler beim Zuweisen des Services:', error);
            throw error;
        }
    }

    // Endkundenpreis eines bestehenden Services setzen/ändern
    async updateEndCustomerPrice(serviceId, price) {
        return axios.put(
            `${this.baseURL}/iaas-service/${serviceId}/endcustomer-price`,
            { endCustomerPrice: price !== '' ? price : null },
            { withCredentials: true }
        );
    }
}

export default new IaaSService();